import axios from "axios";
import { LOG_STORAGE_KEY, MAX_LOG_ENTRIES } from "../const";

export type LogEntry = {
  timestamp: string;
  message: string;
  detail?: string;
};

// Serialize whatever an error handler hands us into a readable string. Errors,
// plain objects, and primitives all show up differently in mobile consoles, so
// normalize them here.
const stringify = (value: unknown): string => {
  if (value instanceof Error) {
    return value.stack || `${value.name}: ${value.message}`;
  }
  if (typeof value === "string") {
    return value;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const read = (): LogEntry[] => {
  try {
    const raw = localStorage.getItem(LOG_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as LogEntry[]) : [];
  } catch {
    return [];
  }
};

const write = (entries: LogEntry[]): void => {
  try {
    localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // localStorage can throw (quota/private mode). Nothing useful to do here;
    // swallow so logging never breaks the app.
  }
};

export const getLogs = (): LogEntry[] => read();

export const clearLogs = (): void => {
  try {
    localStorage.removeItem(LOG_STORAGE_KEY);
  } catch {
    // ignore
  }
};

// Append one error entry, keeping only the most recent MAX_LOG_ENTRIES.
export const logError = (message: unknown, detail?: unknown): void => {
  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    message: stringify(message),
    detail: detail === undefined ? undefined : stringify(detail),
  };
  const entries = read();
  entries.push(entry);
  // Keep the last N only.
  write(entries.slice(-MAX_LOG_ENTRIES));
};

// Log every failed HTTP response (4xx/5xx) and network error at the source,
// before any caller has a chance to swallow it. Both services use the default
// axios instance, so this covers all API calls plus the Cognito token endpoint.
// Re-rejects so existing catch logic is unaffected.
const installAxiosLogging = (): void => {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (axios.isAxiosError(error)) {
        const method = (error.config?.method ?? "get").toUpperCase();
        const url = error.config?.url ?? "unknown";
        const status = error.response
          ? `${error.response.status} ${error.response.statusText}`
          : "network error";
        let body = "";
        try {
          body = error.response?.data
            ? JSON.stringify(error.response.data, null, 2)
            : "";
        } catch {
          body = String(error.response?.data ?? "");
        }
        logError(`API ${status}: ${method} ${url}`, body || error.message);
      }
      return Promise.reject(error);
    },
  );
};

let installed = false;

// Wire up global error capture. Idempotent so React StrictMode's double-invoke
// (or an accidental second call) doesn't double-wrap console.error.
export const initLogging = (): void => {
  if (installed || typeof window === "undefined") {
    return;
  }
  installed = true;

  installAxiosLogging();

  window.addEventListener("error", (event: ErrorEvent) => {
    const where =
      event.filename && event.lineno
        ? ` (${event.filename}:${event.lineno}:${event.colno})`
        : "";
    logError(
      `${event.message}${where}`,
      event.error instanceof Error ? event.error.stack : undefined,
    );
  });

  window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      logError("Unhandled promise rejection", event.reason);
    },
  );

  // Mirror console.error into storage without losing the normal console output.
  const originalConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    try {
      logError(args.map((a) => stringify(a)).join(" "));
    } catch {
      // never let logging break the real console.error call
    }
    originalConsoleError(...args);
  };
};

// eslint-disable-next-line
export default {
  getLogs,
  clearLogs,
  logError,
  initLogging,
};
